import { inject, Injectable } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import dayjs, { Dayjs } from 'dayjs';
import arraySupport from 'dayjs/plugin/arraySupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(weekday);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(arraySupport);
dayjs.extend(duration);

export const DEFAULT_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'L',
    timeInput: 'LT'
  },
  display: {
    dateInput: 'L',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
    timeInput: 'LT',
    timeOptionLabel: 'LT'
  }
};

/**
 * A DateAdapter for working with Dayjs objects, supporting localization.
 */
@Injectable()
export class DayjsDateAdapter extends DateAdapter<Dayjs, string> {

  private readonly localeData: {
    firstDayOfWeek: number,
    daysOfWeek: {
      long: string[],
      short: string[],
      narrow: string[]
    }
    months: {
      long: string[],
      short: string[],
      narrow: string[]
    },
    dates: string[]
  };

  /**
   * Init locale data.
   */
  constructor() {
    super();
    this.setLocale(<string>inject(MAT_DATE_LOCALE));
    this.localeData = {
      firstDayOfWeek: dayjs().localeData().firstDayOfWeek(),
      daysOfWeek: {
        long: dayjs.localeData().weekdays(),
        short: dayjs.localeData().weekdaysShort(),
        narrow: dayjs.localeData().weekdaysMin()
      },
      months: {
        long: dayjs().localeData().months(),
        short: dayjs().localeData().monthsShort(),
        narrow: dayjs().localeData().monthsShort()
      },
      dates: Array.from(new Array(31).keys()).map((_, i) => this.createDate(2017, 0, i + 1).format('D'))
    };
  }

  /**
   * Gets the year component of the given date.
   * @param date The Dayjs object to extract the year from.
   * @returns The year component.
   */
  public getYear(date: Dayjs): number {
    return date.year();
  }

  /**
   * Gets the month component of the given date.
   * @param date The Dayjs object to extract the month from.
   * @returns The month component (0-indexed, 0 = January).
   */
  public getMonth(date: Dayjs): number {
    return date.month();
  }

  /**
   * Gets the date of the month component of the given date.
   * @param date The Dayjs object to extract the date of the month from.
   * @returns The month component (1-indexed, 1 = first of month).
   */
  public getDate(date: Dayjs): number {
    return date.date();
  }

  /**
   * Gets the day of the week component of the given date.
   * @param date The Dayjs object to extract the day of the week from.
   * @returns The month component (0-indexed, 0 = Sunday).
   */
  public getDayOfWeek(date: Dayjs): number {
    return date.day();
  }

  /**
   * Gets a list of names for the months for the current locale.
   * @param style The naming style (e.g. long = 'January', short = 'Jan', narrow = 'J').
   * @returns An ordered list of all month names, starting with January.
   */
  public getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return this.localeData.months[style];
  }

  /**
   * Gets a list of names for the dates of the month for the current locale.
   * @returns An ordered list of all date of the month names, starting with '1'.
   */
  public getDateNames(): string[] {
    return this.localeData.dates;
  }

  /**
   * Gets a list of names for the days of the week for the current locale.
   * @param style The naming style (e.g. long = 'Sunday', short = 'Sun', narrow = 'S').
   * @returns An ordered list of all weekday names, starting with Sunday.
   */
  public getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    return this.localeData.daysOfWeek[style];
  }

  /**
   * Gets the name for the year of the given date.
   * @param date The date to get the year name for.
   * @returns The name of the given year (e.g. '2017').
   */
  public getYearName(date: Dayjs): string {
    return date.format('YYYY');
  }

  /**
   * Gets the first day of the week for the current locale.
   * @returns The first day of the week (0-indexed, 0 = Sunday).
   */
  public getFirstDayOfWeek(): number {
    return this.localeData.firstDayOfWeek;
  }

  /**
   * Gets the number of days in the month of the given date.
   * @param date The Dayjs object whose month should be checked.
   * @returns The number of days in the month of the given Dayjs object.
   */
  public getNumDaysInMonth(date: Dayjs): number {
    return date.daysInMonth();
  }

  /**
   * Clones the given Dayjs object.
   * @param date The Dayjs object to clone
   * @returns A new Dayjs object whose date is the same as the given Dayjs object's.
   */
  public clone(date: Dayjs): Dayjs {
    return date.clone();
  }

  /**
   * Creates a Dayjs object with the given year, month, and date. Does not allow over/under-flow of the
   * month and date.
   * @param year The full year of the date. (e.g. 89 means the year 89, not the year 1989).
   * @param month The month of the date (0-indexed, 0 = January). Must be an integer 0 - 11.
   * @param date The date of month of the date. Must be an integer 1 - length of the given month.
   * @returns The new Dayjs object, or null if invalid.
   */
  public createDate(year: number, month: number, date: number): Dayjs {
    return dayjs([year, month, date]);
  }

  /**
   * Gets today's date as a Dayjs object.
   * @returns Today's date.
   */
  public today(): Dayjs {
    return dayjs();
  }

  /**
   * Parses a date from a user-provided value. Accepts localized formats (e.g. 'LL').
   * @param value The value to parse.
   * @param parseFormat The expected format of the value being parsed.
   * @returns The parsed date as a Dayjs object.
   */
  public parse(value: any, parseFormat: any): Dayjs | null {
    const date = dayjs(value, dayjs.localeData().longDateFormat(parseFormat), this.locale);
    return date.isValid() ? date : null;
  }

  /**
   * Formats a Dayjs object as a string according to the given format.
   * @param date The value to format.
   * @param displayFormat The format to use to display the date as a string.
   * @returns The formatted date string.
   */
  public format(date: Dayjs, displayFormat: string): string {
    return date.format(displayFormat);
  }

  /**
   * Adds the given number of years to the Dayjs object. Years are counted as if flipping 12 pages on the
   * calendar for each year and then finding the closest date in the new month. For example when
   * adding 1 year to Feb 29, 2016, the resulting date will be Feb 28, 2017.
   * @param date The date to add years to.
   * @param years The number of years to add (may be negative).
   * @returns A cloned Dayjs object equal to the given one with the specified number of years added.
   */
  public addCalendarYears(date: Dayjs, years: number): Dayjs {
    return date.add(years, 'year');
  }

  /**
   * Adds the given number of months to the Dayjs object. Months are counted as if flipping a page on the
   * calendar for each month and then finding the closest date in the new month. For example when
   * adding 1 month to Jan 31, 2017, the resulting date will be Feb 28, 2017.
   * @param date The date to add months to.
   * @param months The number of months to add (may be negative).
   * @returns A cloned Dayjs object equal to the given one with the specified number of months added.
   */
  public addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return date.add(months, 'month');
  }

  /**
   * Adds the given number of days to the Dayjs object. Days are counted as if moving one cell on the
   * calendar for each day.
   * @param date The date to add days to.
   * @param days The number of days to add (may be negative).
   * @returns A cloned Dayjs object equal to the given one with the specified number of days added.
   */
  public addCalendarDays(date: Dayjs, days: number): Dayjs {
    return date.add(days, 'day');
  }

  /**
   * Gets the RFC 3339 compatible string (https://tools.ietf.org/html/rfc3339) for the given Dayjs object.
   * This method is used to generate date strings that are compatible with native HTML attributes
   * such as the `min` or `max` attribute of an `<input>`.
   * @param date The date to get the ISO date string for.
   * @returns The ISO date string.
   */
  public toIso8601(date: Dayjs): string {
    return date.toISOString();
  }

  /**
   * Checks whether the given object is Dayjs object.
   * @param obj The object to check
   * @returns Whether the object is a Dayjs object.
   */
  public isDateInstance(obj: any): boolean {
    return dayjs.isDayjs(obj);
  }

  /**
   * Checks whether the given date is a valid Dayjs object.
   * @param date The date to check.
   * @returns Whether the date is a valid Dayjs object.
   */
  public isValid(date: Dayjs): boolean {
    return date.isValid();
  }

  /**
   * Gets an invalid Dayjs object.
   * @returns An invalid Dayjs object.
   */
  public invalid(): Dayjs {
    return dayjs(null);
  }

  /**
   * Sets the locale used for all dates and load the locale in Dayjs. Note: the locale must have been imported into the
   * project in order to be used.
   * @param locale The new locale (e.g. 'fr-CA').
   */
  public override setLocale(locale: string) {
    super.setLocale(locale);
    dayjs.locale(locale);
  }

  /**
   * Sets the time of one date to the time of another.
   * @param target – Date whose time will be set.
   * @param hours – New hours to set on the date object.
   * @param minutes – New minutes to set on the date object.
   * @param seconds – New seconds to set on the date object.
   */
  public override setTime(target: dayjs.Dayjs, hours: number, minutes: number, seconds: number): dayjs.Dayjs {
    if (!target) {
      return target;
    }

    return dayjs(target)
      .hour(hours)
      .minute(minutes)
      .second(seconds)
      .millisecond(0);
  }

  /**
   * Gets the hours component of the given date.
   * @param date – The date to extract the hours from.
   */
  public override getHours(date: dayjs.Dayjs): number {
    return date.hour();
  }

  /**
   * Gets the minutes component of the given date.
   * @param date – The date to extract the minutes from.
   */
  public override getMinutes(date: dayjs.Dayjs): number {
    return date.minute();
  }

  /**
   * Gets the seconds component of the given date.
   * @param date – The date to extract the seconds from.
   */
  public override getSeconds(date: dayjs.Dayjs): number {
    return date.second();
  }

  /**
   * Adds an amount of seconds to the specified date.
   * @param date - Date to which to add the seconds.
   * @param amount - Amount of seconds to add to the date.
   */
  public override addSeconds(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
    return date.add(dayjs.duration(amount, 'seconds'));
  }


  /**
   * Parses a date with a specific time from a user-provided value.
   * @param value - The value to parse.
   * @param parseFormat - The expected format of the value being parsed (type is implementation-dependent).
   */
  public override parseTime(value: any, parseFormat: any): dayjs.Dayjs | null {
    const date = dayjs(value, dayjs.localeData().longDateFormat(parseFormat), this.locale.toLowerCase());
    return date.isValid() ? date : null;
  }
}

/**
 * Provide a DateAdapter for working with Dayjs object in material components that work with dates.
 * @param formats Optional custom formats to use for parsing and formatting dates.
 */
export function provideDayjsDateAdapter(formats: MatDateFormats = DEFAULT_FORMATS) {
  return [
    {provide: DateAdapter, useClass: DayjsDateAdapter},
    {provide: MAT_DATE_FORMATS, useValue: formats}
  ];
}
